import {
  INNOVATION_CATEGORIES, INNOVATION_META, MATURITY_LEVEL, POTENTIALS, POTENTIAL_LABEL,
  innovationMetrics, mergeInnovationBatch,
  type Inovacao as InovacaoContent, type InnovationInsight, type InnovationType, type Potential,
} from '@ads/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { dateTime } from '../lib/format';
import { keys, useConsolidate, useEvidence, useGenerateInsights, useInnovationBatch, useMaterialAction, useSession, useToolReading } from '../lib/queries';
import { useToolDraft } from '../lib/useToolDraft';
import { EvidenceModal, type EvidenceTarget } from './EvidenceModal';
import { ReadingStatus } from './GapsDashboard';

/* ─────────────────────────────────────────────────────────────────────
   10 Tipos de Inovação.
   1 · Diagnóstico: maturidade atual e desejada de cada tipo, evidências,
       o gráfico dos gaps, as maiores oportunidades e a leitura inicial da
       IA — do preenchimento, sem ler as evidências.
   2 · Insights de inovação: um ou mais por tipo, da organização ou da IA.
       A cada evidência lida a IA traz uma rodada nova; a tela a junta à
       lista uma vez só. Tudo editável.
   ───────────────────────────────────────────────────────────────────── */

type Tab = 'diag' | 'ins';
const TYPE_ICON: Record<InnovationType, (p: object) => ReactNode> = {
  profit: Ic.Bars, network: Ic.Nodes, structure: Ic.Branch, process: Ic.Gear, performance: Ic.Cube,
  system: Ic.Layers, service: Ic.Star, channel: Ic.Link, brand: Ic.ShieldCheck, engagement: Ic.Heart,
};
const CAT_ICON = { configuracao: Ic.Gear, oferta: Ic.Cube, experiencia: Ic.Heart } as const;
const newId = () => `ins-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

export function Inovacao() {
  useViewMeta({ nav: 'strategy', crumb: ['Strategy', '10 Tipos de Inovação'], j: .47 });
  const nav = useNavigate();
  const qc = useQueryClient();
  const { projectId } = useSession();
  const { local, commit, flush, saveState, status } = useToolDraft('inovacao');
  const reading = useToolReading('inovacao');
  const batchQ = useInnovationBatch();
  const generate = useGenerateInsights();
  const evidence = useEvidence();
  const act = useMaterialAction();
  const consolidate = useConsolidate();
  const [tab, setTab] = useState<Tab | null>(null);
  const [modal, setModal] = useState<EvidenceTarget | null>(null);

  const c = local;
  const m = useMemo(() => (c ? innovationMetrics(c) : null), [c]);
  const batch = batchQ.data?.reading ?? null;
  const batchBusy = !!batchQ.data && (batchQ.data.pending || batchQ.data.stale);
  const r = reading.data;
  const ini = r?.reading ?? null;
  const iniBusy = !!r && (r.pending || r.stale);

  useEffect(() => { if (m && !tab) setTab(m.complete && c!.insights.length ? 'ins' : 'diag'); }, [m, tab, c]);

  /* uma rodada nova da IA entra na lista, uma vez só */
  useEffect(() => {
    if (!c || !batch || c.batches.includes(batch.id)) return;
    commit(mergeInnovationBatch(c, batch));
  }, [batch, c, commit]);

  /* evidência lida: a rodada de insights pode ter sido agendada — busca de novo */
  const files = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of evidence.data ?? []) if (e.ref?.startsWith('inovacao:')) map.set(e.ref.slice(9), (map.get(e.ref.slice(9)) ?? 0) + 1);
    return map;
  }, [evidence.data]);
  const readCount = (evidence.data ?? []).filter(e => e.ref?.startsWith('inovacao:') && e.reading === 'read').length;
  useEffect(() => { void qc.invalidateQueries({ queryKey: keys.innovationBatch(projectId) }); }, [readCount, qc, projectId]);

  const setType = (key: InnovationType, p: { current?: number; target?: number }) => {
    if (!c) return;
    commit({ ...c, types: c.types.map(t => (t.key === key ? { ...t, ...p } : t)) });
  };
  const patchInsight = (id: string, p: Partial<InnovationInsight>) => { if (c) commit({ ...c, insights: c.insights.map(i => (i.id === id ? { ...i, ...p, ai: false } : i)) }); };
  const addInsight = (type: InnovationType, text = '') => {
    if (!c) return;
    const id = newId();
    commit({ ...c, insights: [...c.insights, { id, type, text, potential: 'medio', ai: false }] });
    /* a linha nova (ou a que acabou de nascer da linha em branco) fica com o foco, com o cursor no fim */
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLTextAreaElement>(`[data-ins="${id}"] textarea`);
      el?.focus(); el?.setSelectionRange(el.value.length, el.value.length);
    });
  };
  const removeInsight = (id: string) => {
    if (!c) return;
    for (const e of evidence.data ?? []) if (e.ref === `inovacao:${id}`) act.mutate({ id: e.id, action: 'delete' });
    commit({ ...c, insights: c.insights.filter(i => i.id !== id) });
  };
  const go = (t: Tab) => { flush(); setTab(t); document.querySelector('.stage')?.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <section className="view on" data-v="inovacao">
      <button className="back" type="button" onClick={() => { flush(); nav('/strategy'); }}><Ic.ChevronLeft />Voltar para Strategy</button>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Strategy</span><b>Ferramenta 07 / 10</b></div>
          <h1>10 Tipos de Inovação</h1>
          <p className="lede">{tab === 'ins'
            ? 'Cadastre insights e oportunidades que podem elevar a maturidade da organização nos tipos prioritários de inovação.'
            : 'Avalie o estágio atual e o desejado de cada tipo de inovação, anexando evidências quando necessário.'}</p>
        </div>
        <div className="tools">
          {status && <span className={`g-save ${saveState}`} aria-live="polite">{saveState === 'saved' && <Ic.Check />}{status}</span>}
        </div>
      </div>

      <div className="bmc-top">
        <ol className="stepper" aria-label="Etapas">
          {([['diag', 'Diagnóstico'], ['ins', 'Insights de inovação']] as const).map(([k, label], i) => (
            <li key={k} className={tab === k ? 'on' : tab === 'ins' && k === 'diag' ? 'done' : ''}>
              <button type="button" onClick={() => go(k)} aria-current={tab === k ? 'step' : undefined}>
                <span className="n">{tab === 'ins' && k === 'diag' ? <Ic.Check /> : i + 1}</span>{label}
              </button>
            </li>
          ))}
        </ol>
        <div className="bmc-align" title={ini?.leitura}>
          <span>Alinhamento ao propósito</span>
          <span className={`vi-bar${iniBusy ? ' is-dim' : ''}`}><i style={{ '--v': `${ini?.alinhamento ?? 0}%` } as CSSProperties} /></span>
          <b>{ini ? `${ini.alinhamento}%` : '—'}</b>
        </div>
      </div>

      {!c || !m || !tab ? <div className="loading-line" /> : tab === 'diag' ? (
        <div className="in-grid">
          <section className="card in-table-card">
            <div className="tablewrap nomask">
              <table className="dense in-table">
                <thead><tr><th colSpan={2}>Tipo de inovação</th><th>Maturidade atual</th><th>Maturidade desejada</th><th>Evidência</th></tr></thead>
                {INNOVATION_CATEGORIES.map(cat => {
                  const CI = CAT_ICON[cat.key];
                  return (
                    <tbody key={cat.key} className={`in-cat c-${cat.key}`}>
                      {cat.types.map((k, n) => {
                        const t = c.types.find(x => x.key === k)!;
                        const TI = TYPE_ICON[k];
                        const count = files.get(k) ?? 0;
                        return (
                          <tr key={k}>
                            {n === 0 && <th rowSpan={cat.types.length} scope="rowgroup" className="in-cat-h"><span><CI />{cat.label}</span></th>}
                            <td className="in-type"><span className="in-ti"><TI /></span><div><b>{INNOVATION_META[k].label}</b><span>{INNOVATION_META[k].desc}</span></div></td>
                            <td className="in-lv" data-label="Atual"><Level value={t.current} label={`Maturidade atual — ${INNOVATION_META[k].label}`} onChange={v => setType(k, { current: v })} /></td>
                            <td className="in-lv" data-label="Desejada"><Level value={t.target} label={`Maturidade desejada — ${INNOVATION_META[k].label}`} onChange={v => setType(k, { target: v })} warn={t.target > 0 && t.current > t.target} /></td>
                            <td className="in-ev">
                              <button type="button" className={`evid g-evid${count ? '' : ' none'}`}
                                onClick={() => setModal(target(k, INNOVATION_META[k].label, 'Evidências · 10 Tipos de Inovação'))}>
                                <Ic.Clip />{count ? `${count} arquivo${count > 1 ? 's' : ''}` : 'Anexar'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  );
                })}
              </table>
            </div>
            <p className="in-foot">{m.rated} de 10 tipos avaliados{m.complete ? ' — a leitura inicial da IA já considera o diagnóstico completo.' : '. A leitura inicial da IA aparece quando os dez estiverem preenchidos.'}</p>
          </section>

          <aside className="in-side">
            <section className="card in-chart-card">
              <h3>Gap de maturidade por tipo de inovação</h3>
              <div className="gd-legend"><span><i className="cur" />Maturidade atual</span><span><i className="tgt" />Maturidade desejada</span><span><i className="gap" />Gap</span></div>
              <div className="in-chart" role="list">
                {m.rows.map(row => (
                  <div key={row.key} className="in-col" role="listitem" aria-label={`${row.label}: atual ${row.current || '—'}, desejada ${row.target || '—'}`}>
                    <div className="in-bars">
                      {row.gap > 0 && <em className="in-gap">+{row.gap}</em>}
                      <span className="b cur" style={{ '--h': `${(row.current / 5) * 100}%` } as CSSProperties} />
                      <span className="b tgt" style={{ '--h': `${(row.target / 5) * 100}%` } as CSSProperties} />
                    </div>
                    <span className="lb" title={row.label}>{row.short}</span>
                  </div>
                ))}
              </div>
            </section>
            <section className="card">
              <h3 className="in-h3">Maiores oportunidades</h3>
              <Opportunities rows={m.top} empty="Preencha as maturidades para ver onde estão os maiores gaps." />
            </section>
            <section className="card in-read">
              <div className="gd-h"><h3 className="in-h3"><Ic.Bulb />Leitura inicial da IA</h3><ReadingStatus updating={iniBusy} basic={r?.basic ?? false} at={r?.updatedAt ?? null} /></div>
              {ini ? <p className={iniBusy ? 'is-dim' : ''}>{ini.leitura}</p>
                : iniBusy ? <div className="obj-skel"><i /><i /><i /></div>
                : <p className="gd-empty">A leitura sai do fim do preenchimento: quando os dez tipos tiverem maturidade atual e desejada.</p>}
              {r?.note && <p className="gd-note"><Ic.Warning />{r.note}</p>}
            </section>
          </aside>
        </div>
      ) : (
        <div className="in-grid">
          <section className="card in-table-card">
            <div className="in-ins-h">
              <div><h3 className="in-h3">Insights e novas possibilidades</h3>
                <p className="subhead">Um ou mais por tipo — o + ao lado do tipo cria outro. Os da IA chegam a cada evidência lida, marcados com <Ic.Sparkle className="in-spark" />; todos são editáveis.</p></div>
              <button type="button" className="btn-ghost sm" onClick={() => generate.mutate()} disabled={generate.isPending || batchBusy || !m.complete}
                title={m.complete ? undefined : 'Complete o diagnóstico primeiro'}>
                {generate.isPending || batchBusy ? <span className="spin" /> : <Ic.Sparkle />}Gerar novos insights</button>
            </div>
            <div className="in-cats">
              {INNOVATION_CATEGORIES.map(cat => {
                const CI = CAT_ICON[cat.key];
                const types = cat.types as readonly InnovationType[];
                const top = [...types].sort((a, b) => (m.rows.find(x => x.key === b)!.gap) - (m.rows.find(x => x.key === a)!.gap))[0]!;
                return (
                  <div key={cat.key} className={`in-cat2 c-${cat.key}`}>
                    <div className="in-cat-b"><CI /><span>{cat.label}</span></div>
                    <div className="in-cat-body">
                      {types.map(k => {
                        const TI = TYPE_ICON[k];
                        const mine = c.insights.filter(i => i.type === k);
                        const row = m.rows.find(x => x.key === k)!;
                        return (
                          <div key={k} className="in-tg">
                            <div className="in-tg-h">
                              <span className="in-ti"><TI /></span>
                              <b>{INNOVATION_META[k].label}</b>
                              {row.gap > 0 && <em className="in-gap-s" title={`Maturidade ${row.current} → ${row.target}`}>+{row.gap}</em>}
                              <button type="button" className="in-tg-add" onClick={() => addInsight(k)} aria-label={`Adicionar insight em ${INNOVATION_META[k].label}`}><Ic.Plus /></button>
                            </div>
                            {(mine.length ? mine : [null]).map(i => (
                              <InsightRow key={i?.id ?? `ghost-${k}`} type={k} insight={i} files={i ? files.get(i.id!) ?? 0 : 0}
                                onCreate={text => addInsight(k, text)} onPatch={p => patchInsight(i!.id!, p)} onRemove={() => removeInsight(i!.id!)}
                                onEvidence={() => i && setModal(target(i.id!, i.text.slice(0, 80) || INNOVATION_META[i.type].label, `Evidência / benchmark · ${INNOVATION_META[i.type].label}`))} />
                            ))}
                          </div>
                        );
                      })}
                      <button type="button" className="addlink" onClick={() => addInsight(top)}><Ic.Plus />Adicionar insight em {cat.label}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <aside className="in-side">
            <section className="card">
              <h3 className="in-h3">Prioridades do diagnóstico</h3>
              <Opportunities rows={m.top} empty="Complete o diagnóstico para ver as prioridades." />
            </section>
            <section className="card in-focus">
              <div className="gd-h"><h3 className="in-h3"><Ic.Sparkle />Focos sugeridos pela IA</h3><ReadingStatus updating={batchBusy} basic={batchQ.data?.basic ?? false} at={batch?.at ?? null} /></div>
              {batch?.focos.length ? (
                <ul className={batchBusy ? 'is-dim' : ''}>{batch.focos.map((f, i) => <li key={i}><Ic.Bulb /><div><b>{f.titulo}</b><span>{f.texto}</span></div></li>)}</ul>
              ) : batchBusy ? <div className="obj-skel"><i /><i /><i /></div> : <p className="gd-empty">Os focos chegam com a primeira rodada de insights da IA.</p>}
              {batch && <p className="in-batch">Última rodada: {dateTime(batch.at)} · {batch.evidences} {batch.evidences === 1 ? 'evidência lida' : 'evidências lidas'}</p>}
              {batchQ.data?.note && <p className="gd-note"><Ic.Warning />{batchQ.data.note}</p>}
            </section>
            <section className="card in-pot">
              <h3 className="in-h3">Potencial adicional</h3>
              {batch ? (
                <div className={`in-pot-b${batchBusy ? ' is-dim' : ''}`}>
                  <PotRing pct={batch.potencial.score} />
                  <div className={`in-pot-t p-${batch.potencial.nivel}`}><b>{POTENTIAL_LABEL[batch.potencial.nivel]} potencial</b><span>{batch.potencial.texto}</span></div>
                </div>
              ) : <p className="gd-empty">O potencial aparece com a primeira rodada de insights.</p>}
            </section>
          </aside>
        </div>
      )}

      {modal && <EvidenceModal target={modal} onClose={() => setModal(null)} />}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => { flush(); nav('/strategy'); }}>Salvar e sair</button>
        <button className="btn-ghost" type="button" onClick={() => (tab === 'ins' ? go('diag') : (flush(), nav('/strategy/bmc')))}>Voltar</button>
        <button className="btn-primary" type="button" disabled={consolidate.isPending}
          onClick={() => { if (tab === 'diag') { go('ins'); return; } flush(); consolidate.mutate(undefined, { onSettled: () => nav('/strategy/macro') }); }}>
          {consolidate.isPending ? 'Consolidando o DNA…' : 'Continuar'}<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}

function target(item: string, title: string, kicker: string): EvidenceTarget {
  return {
    ref: `inovacao:${item}`, title, kicker,
    lede: 'Documentos que mostram o estágio de hoje ou um bom exemplo de fora — relatórios, pesquisas, dados, benchmarks. O ADS lê cada um e, a cada leitura, a IA traz novos insights.',
    empty: 'Com evidência, os insights da IA ficam concretos — apoiados no que o documento mostra.',
  };
}

/* ── peças ────────────────────────────────────────────────────────── */

function Level({ value, onChange, label, warn }: { value: number; onChange: (v: number) => void; label: string; warn?: boolean }) {
  return (
    <span className={`in-sel${value ? ` l${value}` : ''}${warn ? ' warn' : ''}`} title={warn ? 'A desejada está abaixo da atual' : undefined}>
      <select value={value} aria-label={label} onChange={e => onChange(Number(e.target.value))}>
        <option value={0}>—</option>
        {[1, 2, 3, 4, 5].map(l => <option key={l} value={l}>{l} · {MATURITY_LEVEL[l]}</option>)}
      </select>
      <Ic.Caret />
    </span>
  );
}

function Opportunities({ rows, empty }: { rows: Array<{ key: InnovationType; label: string; gap: number; current: number; target: number }>; empty: string }) {
  if (!rows.length) return <p className="gd-empty">{empty}</p>;
  return (
    <ol className="in-opp">
      {rows.map((r, i) => (
        <li key={r.key} title={`${r.label}: de ${r.current} para ${r.target}`}>
          <span className="mono n">{i + 1}</span><span className="nm">{r.label}</span>
          <span className="tr"><i style={{ '--v': `${(r.gap / 4) * 100}%` } as CSSProperties} /></span><b>{r.gap}</b>
        </li>
      ))}
    </ol>
  );
}

function InsightRow({ type, insight, files, onCreate, onPatch, onRemove, onEvidence }: {
  type: InnovationType; insight: InnovationInsight | null; files: number;
  onCreate: (text: string) => void; onPatch: (p: Partial<InnovationInsight>) => void; onRemove: () => void; onEvidence: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { if (!confirm) return; const t = setTimeout(() => setConfirm(false), 3500); return () => clearTimeout(t); }, [confirm]);
  return (
    <div data-ins={insight?.id} className={`in-ir${insight?.ai ? ' ai' : ''}${insight ? '' : ' ghost'}`}>
      <div className="in-ir-t">
        {insight?.ai && <span className="in-ai" title="Sugerido pela IA — edite à vontade"><Ic.Sparkle /></span>}
        <textarea className="gcell" rows={1} maxLength={400} value={insight?.text ?? ''} aria-label={`Insight — ${INNOVATION_META[type].label}`}
          placeholder="Descreva um insight para ampliar este tipo de inovação…"
          onChange={e => (insight ? onPatch({ text: e.target.value }) : e.target.value.trim() && onCreate(e.target.value))} />
      </div>
      {insight ? (
        <div className="in-ir-a">
          <span className={`in-sel pot p-${insight.potential}`} title="Potencial">
            <select value={insight.potential} aria-label="Potencial" onChange={e => onPatch({ potential: e.target.value as Potential })}>
              {POTENTIALS.map(p => <option key={p} value={p}>{POTENTIAL_LABEL[p]}</option>)}
            </select>
            <Ic.Caret />
          </span>
          <button type="button" className={`evid g-evid${files ? '' : ' none'}`} onClick={onEvidence} title="Evidência / benchmark">
            <Ic.Clip />{files ? `${files} arquivo${files > 1 ? 's' : ''}` : 'Anexar'}
          </button>
          {confirm
            ? <button type="button" className="g-del" onClick={onRemove}>Excluir</button>
            : <button type="button" className="va-btn in-x" onClick={() => setConfirm(true)} aria-label="Excluir insight"><Ic.Close /></button>}
        </div>
      ) : null}
    </div>
  );
}

function PotRing({ pct }: { pct: number }) {
  const r = 34, len = 2 * Math.PI * r;
  return (
    <div className="bmc-ring in-ring">
      <svg viewBox="0 0 80 80" aria-hidden="true">
        <circle cx="40" cy="40" r={r} className="bg" />
        <circle cx="40" cy="40" r={r} className="fg" strokeDasharray={`${(pct / 100) * len} ${len}`} transform="rotate(-90 40 40)" />
      </svg>
      <span><b>{pct}</b><small>/100</small></span>
    </div>
  );
}
